import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import * as pdfjsLib from 'pdfjs-dist';
import { EventBus, PDFSinglePageViewer, PDFLinkService } from 'pdfjs-dist/web/pdf_viewer';
import './phpCsReferralViewer.css';
import { FormSubmission } from '../../types';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';
const TEMPLATE_URL = `${API_BASE_URL}/forms/templates/php-cs-referral`;

const PhpCsReferralSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<PDFSinglePageViewer | null>(null);
  const { setValue, getValues } = useFormContext<FormSubmission>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // Text fields render as <input> when single-line and <textarea> when the
  // PDF field is multiline (e.g. the diagnosis/description cells).
  const queryFormWidgets = (root: HTMLElement) =>
    root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input[name], textarea[name]');

  // Read every rendered AcroForm widget from the DOM, keyed by its exact
  // PDF field name, and store it on the shared form state.
  const collectFromViewer = useCallback(() => {
    const root = containerRef.current;
    if (!root) return;

    const flat: Record<string, string> = {};

    queryFormWidgets(root).forEach((el) => {
      const name = el.name;
      if (!name) return;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        const exportValue = el.getAttribute('exportValue') || 'On';
        flat[name] = el.checked ? `/${exportValue}` : '/Off';
      } else {
        flat[name] = el.value ?? '';
      }
    });

    setValue('phpCsReferral_flat', flat, { shouldDirty: true });
  }, [setValue]);

  // Restore any previously saved values (e.g. navigating back to this step).
  const applySavedValues = useCallback(() => {
    const root = containerRef.current;
    if (!root) return;

    const saved = getValues('phpCsReferral_flat');
    if (!saved) return;

    queryFormWidgets(root).forEach((el) => {
      const value = saved[el.name];
      if (value === undefined) return;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        const exportValue = el.getAttribute('exportValue') || 'On';
        el.checked = value === `/${exportValue}`;
      } else {
        el.value = value;
      }
    });
  }, [getValues]);

  useEffect(() => {
    let cancelled = false;
    let pdfDocument: pdfjsLib.PDFDocumentProxy | null = null;
    const container = containerRef.current;
    if (!container) return;

    // Reset the container in case a previous instance (e.g. React StrictMode's
    // extra dev-mode mount/unmount cycle) left DOM behind, then give
    // PDFSinglePageViewer the empty .pdfViewer div it requires as its first child.
    container.innerHTML = '';
    const viewerDiv = document.createElement('div');
    viewerDiv.className = 'pdfViewer';
    container.appendChild(viewerDiv);

    const eventBus = new EventBus();
    const linkService = new PDFLinkService({ eventBus });
    const viewer = new PDFSinglePageViewer({
      container,
      eventBus,
      linkService,
      annotationMode: pdfjsLib.AnnotationMode.ENABLE_FORMS,
    });
    linkService.setViewer(viewer);
    viewerRef.current = viewer;

    const onFieldChange = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        collectFromViewer();
      }
    };
    container.addEventListener('input', onFieldChange, true);
    container.addEventListener('change', onFieldChange, true);

    // "pagesloaded" only means the page canvas/layout is ready — the actual
    // AcroForm <input>/<textarea> widgets are built by the annotation layer,
    // which renders asynchronously afterward and fires its own event. We only
    // ever *apply* saved values here — never collect — since collecting before
    // all fields exist would overwrite the shared flat map with an incomplete
    // snapshot. Collection only happens in response to real user input.
    const onAnnotationLayerRendered = () => {
      if (cancelled) return;
      applySavedValues();
      setStatus('ready');
    };
    eventBus.on('annotationlayerrendered', onAnnotationLayerRendered);

    pdfjsLib.getDocument(TEMPLATE_URL).promise
      .then((doc) => {
        if (cancelled) {
          doc.destroy();
          return;
        }
        pdfDocument = doc;
        linkService.setDocument(doc, null);
        viewer.setDocument(doc);
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      container.removeEventListener('input', onFieldChange, true);
      container.removeEventListener('change', onFieldChange, true);
      eventBus.off('annotationlayerrendered', onAnnotationLayerRendered);
      viewer.cleanup();
      pdfDocument?.destroy();
      container.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAllFields = () => {
    const root = containerRef.current;
    if (!root) return;
    queryFormWidgets(root).forEach((el) => {
      if (el instanceof HTMLInputElement && el.type === 'checkbox') el.checked = false;
      else el.value = '';
    });
    collectFromViewer();
  };

  return (
    <div className="space-y-4">
      <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        Fill out the referral form below by typing directly into its fields. Your entries are saved automatically as you type.
      </div>

      {status === 'error' && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          Failed to load the referral form PDF. Please refresh and try again.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={clearAllFields}
          className="text-sm text-gray-600 hover:text-gray-900 underline"
        >
          Clear all fields
        </button>
      </div>

      <div
        className="relative border border-gray-300 rounded overflow-auto bg-gray-100"
        style={{ height: '80vh' }}
      >
        <div ref={containerRef} className="absolute inset-0 overflow-auto" />
      </div>
    </div>
  );
};

export default PhpCsReferralSection;
